import { ApiReturnResource } from '@highimpact-frontend/api-types';
import { CustomizedDialog } from '@highimpact/dashboard-ui';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { LegacyRef, useEffect, useMemo, useState } from 'react';
import { useDashboardHost } from '../../../../../context/DashboardHostContext';
import {
  useDashboardLibraryDispatch,
  useDashboardLibrarySelector,
} from '../../../../../runtime/DashboardLibraryProvider';
import { setWidgetConfigFormIsOpen } from '../../../../../state/dashboard/slice';
import { persistImageWidgetResourceAssignment } from '../assignDashboardElementResource';
import CreateImageResource from './CreateImageResource';
import { ApiReturnImageResource } from './ImageDisplayer';
import ImageSelector from './ImageSelector';

interface ImageWidgetConfigContentProps {
  site_id: number;
  project_id: number;
  grid_type: 'reports' | 'grids';
  ref: React.LegacyRef<HTMLDivElement>;
}

const ImageWidgetConfigContent: React.FC<ImageWidgetConfigContentProps> = (
  { site_id, project_id, grid_type },
  _ref: LegacyRef<HTMLDivElement>
) => {
  const [images, setImages] = useState<ApiReturnImageResource[]>([]);
  const [selectedImage, setSelectedImage] = useState<ApiReturnImageResource>();
  const [addImageModalOpen, setAddImageModalOpen] = useState<boolean>(false);
  const [siteChecked, setSiteChecked] = useState<boolean>(true);

  const dispatch = useDashboardLibraryDispatch();
  const host = useDashboardHost();

  const config = useDashboardLibrarySelector(
    (state) => state.dashboard.resourceSelectorConfig
  );
  const widgetContext = useDashboardLibrarySelector((state) => {
    if (config.state === 'closed') {
      return undefined;
    }

    if (!state.dashboard[grid_type][config.dashboardId]) {
      return undefined;
    }

    const widget =
      state.dashboard[grid_type][config.dashboardId].widgets[config.widgetId];
    if (!widget) {
      return undefined;
    }

    return {
      widgetId: config.widgetId,
      gridId: config.dashboardId,
      dashboardElementId: widget.dashboardElementId,
      elementData: widget.elementData,
    };
  });

  const params = useMemo(
    () => ({
      project_id: !siteChecked ? project_id : null,
      site_id: siteChecked ? site_id : null,
      device_id: null as null,
    }),
    [siteChecked, project_id, site_id]
  );

  const closeModal = () => {
    dispatch(
      setWidgetConfigFormIsOpen({
        state: 'closed',
      })
    );
  };

  const selectImage = async () => {
    if (!selectedImage) {
      return;
    }

    if (
      config.state === 'open' &&
      widgetContext?.dashboardElementId &&
      widgetContext.elementData
    ) {
      try {
        await persistImageWidgetResourceAssignment(
          host.api.dashboard,
          dispatch,
          {
            gridType: config.grid_type,
            gridId: widgetContext.gridId,
            widgetId: widgetContext.widgetId,
            dashboardElementId: widgetContext.dashboardElementId,
            resourceId: selectedImage.id,
            elementData: widgetContext.elementData,
          }
        );
      } catch (error) {
        host.showNotification(
          "Impossible d'associer l'image au widget.",
          'error'
        );
        console.error('Error assigning image resource:', error);
        return;
      }
    }

    closeModal();
  };

  const handleImageSelection = (image: ApiReturnImageResource) => {
    setSelectedImage(image);
  };

  useEffect(() => {
    let unmounted = false;

    host.api.resource.getByEntity(params).then((response) => {
      if (unmounted) return;
      const resources = response as ApiReturnResource[];
      const blobs = resources.filter((rsc) => rsc.resource_type === 'blob');
      setImages(blobs as ApiReturnImageResource[]);
    });

    return () => {
      unmounted = true;
    };
  }, [host.api.resource, params]);

  return (
    <>
      <CustomizedDialog
        open={config.state === 'open'}
        onCancel={closeModal}
        title="Configuration"
        dialogMaxWidth="480px"
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              p: 2,
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2">Créer une nouvelle image</Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setAddImageModalOpen(true)}
            >
              Ajouter
            </Button>
          </Box>
          <Stack direction="row" justifyContent="center" sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={siteChecked}
                  onChange={(_event, checked) => setSiteChecked(checked)}
                />
              }
              label="Pour le site"
            />
          </Stack>
          {images.length !== 0 && (
            <ImageSelector
              images={images}
              defaultValue={selectedImage ? selectedImage.id : null}
              onSelect={handleImageSelection}
            />
          )}
          <Button
            variant="contained"
            onClick={selectImage}
            sx={{ mt: 3 }}
            disabled={selectedImage === undefined}
            fullWidth
          >
            Sélectionner
          </Button>
        </Box>
      </CustomizedDialog>
      <CreateImageResource
        isModalOpen={addImageModalOpen}
        handleModalOpen={() => setAddImageModalOpen(false)}
        project_id={project_id}
        site_id={site_id}
        grid_type={grid_type}
        afterCreate={closeModal}
      />
    </>
  );
};

export default ImageWidgetConfigContent;
